import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatPrice, formatDate } from '../../../utils';

const FinancialOverview = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('month');

  const [financialData, setFinancialData] = useState({
    totalRevenue: 45600,
    subscriptionRevenue: 12500,
    commissionRevenue: 33100,
    pendingPayouts: 8900,
    processedPayouts: 36700,
    monthlyGrowth: 12.5,
    activeSubscriptions: 89,
    totalTransactions: 234
  });

  const [recentTransactions, setRecentTransactions] = useState([
    {
      id: 1,
      vendor: 'Rajesh Photography Studio',
      type: 'commission',
      amount: 450,
      status: 'completed',
      date: '2024-03-15',
      description: 'Wedding booking commission'
    },
    {
      id: 2,
      vendor: 'Golden Mandap Decorators',
      type: 'subscription',
      amount: 99,
      status: 'completed',
      date: '2024-03-14',
      description: 'Monthly premium subscription'
    },
    {
      id: 3,
      vendor: 'Mehndi by Priya',
      type: 'commission',
      amount: 240,
      status: 'pending',
      date: '2024-03-13',
      description: 'Henna service commission'
    }
  ]);

  const [subscriptionBreakdown, setSubscriptionBreakdown] = useState([
    { plan: 'Free', count: 156, revenue: 0, percentage: 63 },
    { plan: 'Basic', count: 67, revenue: 3350, percentage: 27 },
    { plan: 'Premium', count: 22, revenue: 2180, percentage: 9 },
    { plan: 'Enterprise', count: 3, revenue: 1800, percentage: 1 }
  ]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-600';
      case 'pending':
        return 'bg-yellow-100 text-yellow-600';
      case 'failed':
        return 'bg-red-100 text-red-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'commission':
        return 'Percent';
      case 'subscription':
        return 'CreditCard';
      default:
        return 'DollarSign';
    }
  };

  const periods = [
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'This Quarter' },
    { value: 'year', label: 'This Year' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-2xl text-text-primary">
          Financial Overview
        </h2>
        <div className="flex items-center space-x-4">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
          >
            {periods.map((period) => (
              <option key={period.value} value={period.value}>
                {period.label}
              </option>
            ))}
          </select>
          <Button variant="primary" iconName="Download">
            Export Report
          </Button>
        </div>
      </div>

      {/* Revenue Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-text-secondary text-sm font-medium">Total Revenue</p>
              <p className="text-2xl font-bold text-text-primary mt-1">
                {formatPrice(financialData.totalRevenue)}
              </p>
              <p className="text-sm text-green-600 mt-1">
                +{financialData.monthlyGrowth}% from last month
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Icon name="DollarSign" size={24} className="text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-text-secondary text-sm font-medium">Subscription Revenue</p>
              <p className="text-2xl font-bold text-text-primary mt-1">
                {formatPrice(financialData.subscriptionRevenue)}
              </p>
              <p className="text-sm text-text-muted mt-1">
                {financialData.activeSubscriptions} active subscriptions
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Icon name="CreditCard" size={24} className="text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-text-secondary text-sm font-medium">Commission Revenue</p>
              <p className="text-2xl font-bold text-text-primary mt-1">
                {formatPrice(financialData.commissionRevenue)}
              </p>
              <p className="text-sm text-text-muted mt-1">
                {financialData.totalTransactions} transactions
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Icon name="Percent" size={24} className="text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-text-secondary text-sm font-medium">Pending Payouts</p>
              <p className="text-2xl font-bold text-text-primary mt-1">
                {formatPrice(financialData.pendingPayouts)}
              </p>
              <p className="text-sm text-text-muted mt-1">
                Processed: {formatPrice(financialData.processedPayouts)}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <Icon name="Clock" size={24} className="text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Breakdown */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <h3 className="font-heading font-semibold text-lg text-text-primary mb-4">
          Subscription Breakdown
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {subscriptionBreakdown.map((plan, index) => (
            <div key={index} className="p-4 border border-border-light rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-text-primary">{plan.plan}</h4>
                <span className="text-sm text-text-secondary">{plan.percentage}%</span>
              </div>
              <div className="text-2xl font-bold text-text-primary mb-1">
                {plan.count}
              </div>
              <div className="text-sm text-text-secondary">
                {formatPrice(plan.revenue)}/month
              </div>
              <div className="w-full bg-surface-200 rounded-full h-2 mt-2">
                <div 
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${plan.percentage}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        <div className="px-6 py-4 border-b border-border-light">
          <h3 className="font-heading font-semibold text-lg text-text-primary">
            Recent Transactions
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-surface-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Vendor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-border-light">
              {recentTransactions.map((transaction) => (
                <tr key={transaction.id} className="hover:bg-surface-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center">
                        <Icon name={getTypeIcon(transaction.type)} size={20} className="text-white" />
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-text-primary">{transaction.vendor}</div>
                        <div className="text-sm text-text-secondary">{transaction.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                    {transaction.type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-text-primary">
                    {formatPrice(transaction.amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(transaction.status)}`}>
                      {transaction.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                    {formatDate(transaction.date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <Button variant="ghost" size="sm">
                      View Details
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FinancialOverview;